import React, {
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  import axios from "axios";
  import { toast } from "react-hot-toast";
  
  import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
  } from "@mui/material";
  
  import AddRoundedIcon from "@mui/icons-material/AddRounded";
  import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
  import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
  import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
  import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
  import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
  import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
  import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
  import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
  import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
  
  import { useStyle } from "../../context/styleContext/styleContext";
  import Seo from "../../components/Seo";
  
  const ENDPOINTS = {
    products: "/api/get-products-available-on-website",
    list: "/admin/get-redemption-codes",
    generate: "/admin/generate-redemption-codes",
    updateStatus: "/admin/update-redemption-code-status",
    export: "/admin/export-redemption-codes",
  };
  
  const INITIAL_FILTERS = {
    search: "",
    status: "all",
    source: "all",
    sku: "all",
    batchId: "",
  };
  
  const INITIAL_GENERATOR = {
    source: "KVR",
    sku: "",
    count: 25,
    prefix: "KVR",
    batchId: "",
    expiresAt: "",
    notes: "",
  };
  
  function formatDate(value) {
    if (!value) {
      return "—";
    }
  
    const date = new Date(value);
  
    if (Number.isNaN(date.getTime())) {
      return "—";
    }
  
    return date.toLocaleString();
  }
  
  function getStatusChip(status) {
    switch (status) {
      case "available":
        return {
          label: "Available",
          backgroundColor: "rgba(30,190,90,0.16)",
          borderColor: "rgba(30,200,90,0.34)",
          color: "rgb(80,225,125)",
        };
  
      case "redeemed":
        return {
          label: "Redeemed",
          backgroundColor: "rgba(55,135,225,0.16)",
          borderColor: "rgba(75,155,245,0.34)",
          color: "rgb(115,180,255)",
        };
  
      case "disabled":
        return {
          label: "Disabled",
          backgroundColor: "rgba(220,70,70,0.16)",
          borderColor: "rgba(235,90,90,0.34)",
          color: "rgb(255,130,130)",
        };
  
      default:
        return {
          label: status || "Unknown",
          backgroundColor: "rgba(255,255,255,0.08)",
          borderColor: "rgba(255,255,255,0.14)",
          color: "inherit",
        };
    }
  }
  
  function downloadTextFile({
    contents,
    filename,
    type = "text/plain;charset=utf-8",
  }) {
    const blob = new Blob([contents], {
      type,
    });
  
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
  
    anchor.href = url;
    anchor.download = filename;
  
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  
    URL.revokeObjectURL(url);
  }
  
  export default function AdminRedemptionCodesPage() {
    const { style } = useStyle();
  
    const token = useMemo(
      () => localStorage.getItem("token") || "",
      []
    );
  
    const [products, setProducts] = useState([]);
    const [codes, setCodes] = useState([]);
  
    const [stats, setStats] = useState({
      total: 0,
      available: 0,
      redeemed: 0,
      disabled: 0,
    });
  
    const [filterOptions, setFilterOptions] = useState({
      sources: [],
      batches: [],
    });
  
    const [filters, setFilters] = useState(
      INITIAL_FILTERS
    );
  
    const [appliedFilters, setAppliedFilters] = useState(
      INITIAL_FILTERS
    );
  
    const [generator, setGenerator] = useState(
      INITIAL_GENERATOR
    );
  
    const [pagination, setPagination] = useState({
      page: 1,
      limit: 25,
      total: 0,
      pages: 1,
    });
  
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [updatingCodeId, setUpdatingCodeId] =
      useState("");
  
    const [generatedBatch, setGeneratedBatch] =
      useState(null);
  
    const [disableDialog, setDisableDialog] = useState({
      open: false,
      code: null,
      reason: "",
    });
  
    const panelBg =
      style.darkPanel || "rgba(9,12,18,0.45)";
  
    const sectionCardSx = {
      p: {
        xs: 1.5,
        sm: 2,
      },
      borderRadius: "14px",
      backgroundColor: panelBg,
      border: "1px solid rgba(255,255,255,0.08)",
    };
  
    const sharedTextFieldSx = {
      width: "100%",
  
      "& .MuiInputBase-root": {
        color: style.color4,
        backgroundColor: style.color2,
        borderRadius: "8px",
      },
  
      "& .MuiOutlinedInput-root fieldset": {
        borderColor: style.color2,
      },
  
      "& .MuiOutlinedInput-root:hover fieldset": {
        borderColor: style.color3,
      },
  
      "& .MuiOutlinedInput-root.Mui-focused fieldset": {
        borderColor: style.accentGreen,
      },
  
      "& .MuiInputLabel-root": {
        color: style.color3,
      },
  
      "& .MuiInputLabel-root.Mui-focused": {
        color: style.color4,
      },
  
      "& .MuiFormHelperText-root": {
        color: style.color3,
        marginLeft: 0,
      },
  
      "& .MuiSelect-icon": {
        color: style.color3,
      },
    };
  
    const productMap = useMemo(() => {
      const map = new Map();
  
      for (const product of products) {
        map.set(String(product.SKU), product);
      }
  
      return map;
    }, [products]);
  
    const requireToken = () => {
      if (token) {
        return true;
      }
  
      toast.error("You must be logged in as an admin.");
      return false;
    };
  
    const loadProducts = async () => {
      try {
        const { data } = await axios.post(
          ENDPOINTS.products
        );
  
        const productList = Array.isArray(data)
          ? data
          : data?.products || [];
  
        productList.sort((a, b) =>
          String(a.name || "").localeCompare(
            String(b.name || "")
          )
        );
  
        setProducts(productList);
  
        setGenerator((previous) => {
          if (
            previous.sku ||
            productList.length === 0
          ) {
            return previous;
          }
  
          return {
            ...previous,
            sku: String(productList[0].SKU),
          };
        });
      } catch (error) {
        console.error(
          "Unable to retrieve products:",
          error
        );
  
        toast.error(
          error?.response?.data?.error ||
            "The product list could not be loaded."
        );
      }
    };
  
    const loadCodes = async ({
      page = 1,
      activeFilters = appliedFilters,
    } = {}) => {
      if (!requireToken()) {
        return;
      }
  
      try {
        setLoading(true);
  
        const { data } = await axios.post(
          ENDPOINTS.list,
          {
            token,
            page,
            limit: pagination.limit,
            ...activeFilters,
          }
        );
  
        if (data?.error) {
          toast.error(data.error);
          return;
        }
  
        setCodes(
          Array.isArray(data?.codes)
            ? data.codes
            : []
        );
  
        setStats(
          data?.stats || {
            total: 0,
            available: 0,
            redeemed: 0,
            disabled: 0,
          }
        );
  
        setFilterOptions(
          data?.filterOptions || {
            sources: [],
            batches: [],
          }
        );
  
        setPagination((previous) => ({
          ...previous,
          ...(data?.pagination || {}),
        }));
      } catch (error) {
        console.error(
          "Unable to retrieve redemption codes:",
          error
        );
  
        toast.error(
          error?.response?.data?.error ||
            "The redemption codes could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
  
      loadProducts();
  
      loadCodes({
        page: 1,
        activeFilters: INITIAL_FILTERS,
      });
  
      // The token and initial filters do not change while mounted.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    const handleGeneratorChange = (event) => {
      const { name, value } = event.target;
  
      setGenerator((previous) => ({
        ...previous,
        [name]: value,
      }));
    };
  
    const handleFilterChange = (event) => {
      const { name, value } = event.target;
  
      setFilters((previous) => ({
        ...previous,
        [name]: value,
      }));
    };
  
    const handleFilterSubmit = async (event) => {
      event.preventDefault();
  
      setAppliedFilters(filters);
  
      await loadCodes({
        page: 1,
        activeFilters: filters,
      });
    };
  
    const clearFilters = async () => {
      setFilters(INITIAL_FILTERS);
      setAppliedFilters(INITIAL_FILTERS);
  
      await loadCodes({
        page: 1,
        activeFilters: INITIAL_FILTERS,
      });
    };
  
    const handlePageChange = async (
      event,
      newPage
    ) => {
      await loadCodes({
        page: newPage,
        activeFilters: appliedFilters,
      });
    };
  
    const generateCodes = async () => {
      if (!requireToken()) {
        return;
      }
  
      const source = String(generator.source || "")
        .trim()
        .toLowerCase();
  
      const prefix = String(generator.prefix || "")
        .trim()
        .toUpperCase();
  
      const count = Number(generator.count);
  
      if (!source) {
        toast.error("Enter a marketplace source.");
        return;
      }
  
      if (!generator.sku) {
        toast.error("Select a product.");
        return;
      }
  
      if (
        !Number.isInteger(count) ||
        count < 1 ||
        count > 5000
      ) {
        toast.error(
          "The number of codes must be between 1 and 5,000."
        );
  
        return;
      }
  
      try {
        setGenerating(true);
  
        const { data } = await axios.post(
          ENDPOINTS.generate,
          {
            token,
            source,
            sku: generator.sku,
            count,
            prefix,
            batchId: String(
              generator.batchId || ""
            ).trim(),
            expiresAt: generator.expiresAt || null,
            notes: String(generator.notes || "").trim(),
          }
        );
  
        if (data?.error) {
          toast.error(data.error);
          return;
        }
  
        setGeneratedBatch(data);
  
        const nextFilters = {
          ...INITIAL_FILTERS,
          batchId: data.batchId,
        };
  
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
  
        toast.success(
          `${data.count} redemption code${
            data.count === 1 ? "" : "s"
          } generated.`
        );
  
        await loadCodes({
          page: 1,
          activeFilters: nextFilters,
        });
      } catch (error) {
        console.error(
          "Unable to generate redemption codes:",
          error
        );
  
        toast.error(
          error?.response?.data?.error ||
            "The redemption codes could not be generated."
        );
      } finally {
        setGenerating(false);
      }
    };
  
    const copyText = async (
      value,
      successMessage
    ) => {
      try {
        await navigator.clipboard.writeText(value);
        toast.success(successMessage);
      } catch (error) {
        console.error("Clipboard copy failed:", error);
        toast.error("The text could not be copied.");
      }
    };
  
    const downloadGeneratedBatch = () => {
      if (!generatedBatch?.csv) {
        return;
      }
  
      downloadTextFile({
        contents: generatedBatch.csv,
        filename: `${generatedBatch.batchId}.csv`,
        type: "text/csv;charset=utf-8",
      });
    };
  
    const exportFilteredCodes = async () => {
      if (!requireToken()) {
        return;
      }
  
      try {
        setExporting(true);
  
        const response = await axios.post(
          ENDPOINTS.export,
          {
            token,
            ...appliedFilters,
          },
          {
            responseType: "blob",
          }
        );
  
        const contentDisposition =
          response.headers["content-disposition"] || "";
  
        const filenameMatch = contentDisposition.match(
          /filename="?([^"]+)"?/i
        );
  
        const filename =
          filenameMatch?.[1] ||
          "redemption-codes.csv";
  
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
  
        anchor.href = url;
        anchor.download = filename;
  
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
  
        URL.revokeObjectURL(url);
  
        toast.success(
          "The redemption codes were exported."
        );
      } catch (error) {
        console.error(
          "Unable to export redemption codes:",
          error
        );
  
        toast.error(
          "The redemption codes could not be exported."
        );
      } finally {
        setExporting(false);
      }
    };
  
    const openDisableDialog = (code) => {
      setDisableDialog({
        open: true,
        code,
        reason: "",
      });
    };
  
    const closeDisableDialog = () => {
      setDisableDialog({
        open: false,
        code: null,
        reason: "",
      });
    };
  
    const updateCodeStatus = async ({
      code,
      status,
      reason = "",
    }) => {
      if (!requireToken() || !code?._id) {
        return;
      }
  
      try {
        setUpdatingCodeId(code._id);
  
        const { data } = await axios.post(
          ENDPOINTS.updateStatus,
          {
            token,
            redemptionCodeId: code._id,
            status,
            reason: String(reason || "").trim(),
          }
        );
  
        if (data?.error) {
          toast.error(data.error);
          return;
        }
  
        toast.success(
          data?.message ||
            "The redemption code was updated."
        );
  
        closeDisableDialog();
  
        await loadCodes({
          page: pagination.page,
          activeFilters: appliedFilters,
        });
      } catch (error) {
        console.error(
          "Unable to update redemption code:",
          error
        );
  
        toast.error(
          error?.response?.data?.error ||
            "The redemption code could not be updated."
        );
      } finally {
        setUpdatingCodeId("");
      }
    };
  
    const statCards = [
      {
        label: "Total Codes",
        value: stats.total,
        icon: <KeyRoundedIcon />,
      },
      {
        label: "Available",
        value: stats.available,
        icon: <Inventory2RoundedIcon />,
      },
      {
        label: "Redeemed",
        value: stats.redeemed,
        icon: <RedeemRoundedIcon />,
      },
      {
        label: "Disabled",
        value: stats.disabled,
        icon: <BlockRoundedIcon />,
      },
    ];
  
    return (
      <>
        <Seo
          title="Redemption Codes | Admin | Parish Audio"
          description="Generate and manage Parish Audio marketplace redemption codes."
          canonical="https://parishaudio.com/admin/redemption-codes"
          robots="noindex,nofollow"
        />
  
        <Box
          sx={{
            backgroundColor: "black",
            minHeight: "100vh",
            color: style.color4,
            px: {
              xs: 1.25,
              sm: 1.5,
              md: 2.5,
            },
            py: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Box
            sx={{
              maxWidth: 1500,
              mx: "auto",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
              }}
            >
              <Box
                sx={{
                  px: {
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                  },
                  py: {
                    xs: 2.25,
                    md: 3,
                  },
                  backgroundColor: panelBg,
                  borderBottom: `1px solid ${style.color2}`,
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    md: "row",
                  }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{
                    xs: "flex-start",
                    md: "center",
                  }}
                >
                  <Stack
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Chip
                      icon={<KeyRoundedIcon />}
                      label="Admin Redemption Codes"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor:
                          "rgba(255,255,255,0.06)",
                      }}
                    />
  
                    <Typography
                      sx={{
                        color: style.color4,
                        fontSize: {
                          xs: "1.9rem",
                          md: "2.5rem",
                        },
                        fontWeight: 800,
                        lineHeight: 1.05,
                        letterSpacing: "-0.03em",
                      }}
                    >
                      Redemption Code Manager
                    </Typography>
  
                    <Typography
                      sx={{
                        color: style.color3,
                        lineHeight: 1.65,
                      }}
                    >
                      Generate, search, export, disable,
                      and reactivate marketplace redemption
                      codes.
                    </Typography>
                  </Stack>
  
                  <Button
                    startIcon={<RefreshRoundedIcon />}
                    onClick={() =>
                      loadCodes({
                        page: pagination.page,
                        activeFilters: appliedFilters,
                      })
                    }
                    disabled={loading}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor:
                        "rgba(255,255,255,0.05)",
                      border:
                        "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Box>
  
              <Box
                sx={{
                  px: {
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                  },
                  py: 2.5,
                }}
              >
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    {statCards.map((card) => (
                      <Grid
                        item
                        xs={6}
                        md={3}
                        key={card.label}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...sectionCardSx,
                            height: "100%",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                color: style.color3,
                              }}
                            >
                              {card.icon}
                            </Box>
  
                            <Box>
                              <Typography
                                sx={{
                                  color: style.color3,
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                {card.label}
                              </Typography>
  
                              <Typography
                                sx={{
                                  color: style.color4,
                                  fontSize: "1.65rem",
                                  fontWeight: 900,
                                }}
                              >
                                {card.value}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
  
                  <Paper
                    elevation={0}
                    sx={sectionCardSx}
                  >
                    <Stack spacing={1.75}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <AddRoundedIcon
                          sx={{
                            color: style.color3,
                          }}
                        />
  
                        <Typography
                          sx={{
                            color: style.color4,
                            fontSize: "1.2rem",
                            fontWeight: 800,
                          }}
                        >
                          Generate a Code Batch
                        </Typography>
                      </Stack>
  
                      <Grid container spacing={2}>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={3}
                        >
                          <TextField
                            select
                            name="sku"
                            label="Product"
                            value={generator.sku}
                            onChange={handleGeneratorChange}
                            size="small"
                            sx={sharedTextFieldSx}
                          >
                            {products.map((product) => (
                              <MenuItem
                                key={product.SKU}
                                value={String(product.SKU)}
                              >
                                {product.name} ({product.SKU})
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={3}
                        >
                          <TextField
                            name="source"
                            label="Marketplace"
                            value={generator.source}
                            onChange={handleGeneratorChange}
                            placeholder="KVR"
                            size="small"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={3}
                        >
                          <TextField
                            name="prefix"
                            label="Code Prefix"
                            value={generator.prefix}
                            onChange={handleGeneratorChange}
                            placeholder="KVR"
                            size="small"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={3}
                        >
                          <TextField
                            name="count"
                            label="Number of Codes"
                            type="number"
                            value={generator.count}
                            onChange={handleGeneratorChange}
                            inputProps={{
                              min: 1,
                              max: 5000,
                            }}
                            size="small"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
  
                        <Grid item xs={12} md={8}>
                          <TextField
                            name="batchId"
                            label="Batch ID"
                            value={generator.batchId}
                            onChange={handleGeneratorChange}
                            placeholder="Leave blank to generate automatically"
                            size="small"
                            helperText="Example: kvr-auto-level-2026-07"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
  
                        <Grid item xs={12} md={4}>
                          <TextField
                            name="expiresAt"
                            label="Code Expiration"
                            type="date"
                            value={generator.expiresAt}
                            onChange={handleGeneratorChange}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            size="small"
                            helperText="Leave blank for no expiration"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
  
                        <Grid item xs={12}>
                          <TextField
                            name="notes"
                            label="Batch Notes"
                            value={generator.notes}
                            onChange={handleGeneratorChange}
                            multiline
                            minRows={2}
                            size="small"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
                      </Grid>
  
                      <Box>
                        <Button
                          onClick={generateCodes}
                          disabled={generating}
                          startIcon={
                            generating ? (
                              <CircularProgress
                                size={18}
                                sx={{
                                  color: "inherit",
                                }}
                              />
                            ) : (
                              <AddRoundedIcon />
                            )
                          }
                          sx={{
                            minHeight: 44,
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: style.color4,
                            backgroundColor:
                              style.accentGreen,
                          }}
                        >
                          {generating
                            ? "Generating..."
                            : "Generate Codes"}
                        </Button>
                      </Box>
  
                      {generatedBatch && (
                        <Alert
                          severity="success"
                          sx={{
                            color: style.color4,
                            backgroundColor:
                              "rgba(30,190,90,0.12)",
                            border:
                              "1px solid rgba(30,200,90,0.25)",
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              md: "row",
                            }}
                            spacing={1}
                            alignItems={{
                              xs: "flex-start",
                              md: "center",
                            }}
                            justifyContent="space-between"
                          >
                            <Typography>
                              Generated{" "}
                              {generatedBatch.count} code
                              {generatedBatch.count === 1
                                ? ""
                                : "s"}{" "}
                              in batch{" "}
                              <strong>
                                {generatedBatch.batchId}
                              </strong>
                              .
                            </Typography>
  
                            <Stack
                              direction={{
                                xs: "column",
                                sm: "row",
                              }}
                              spacing={0.75}
                            >
                              <Button
                                onClick={() =>
                                  copyText(
                                    generatedBatch.codes.join(
                                      "\n"
                                    ),
                                    "All generated codes copied."
                                  )
                                }
                                startIcon={
                                  <ContentCopyRoundedIcon />
                                }
                                size="small"
                                sx={{
                                  color: style.color4,
                                }}
                              >
                                Copy Codes
                              </Button>
  
                              <Button
                                onClick={downloadGeneratedBatch}
                                startIcon={
                                  <DownloadRoundedIcon />
                                }
                                size="small"
                                sx={{
                                  color: style.color4,
                                }}
                              >
                                Download CSV
                              </Button>
                            </Stack>
                          </Stack>
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
  
                  <Paper
                    component="form"
                    onSubmit={handleFilterSubmit}
                    elevation={0}
                    sx={sectionCardSx}
                  >
                    <Stack spacing={1.5}>
                      <Typography
                        sx={{
                          color: style.color4,
                          fontSize: "1.2rem",
                          fontWeight: 800,
                        }}
                      >
                        Search and Filter
                      </Typography>
  
                      <Grid container spacing={2}>
                        <Grid item xs={12} lg={4}>
                          <TextField
                            name="search"
                            label="Search Codes"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Code, email, product, batch..."
                            size="small"
                            sx={sharedTextFieldSx}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchRoundedIcon
                                    sx={{
                                      color: style.color3,
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={2}
                        >
                          <TextField
                            select
                            name="status"
                            label="Status"
                            value={filters.status}
                            onChange={handleFilterChange}
                            size="small"
                            sx={sharedTextFieldSx}
                          >
                            <MenuItem value="all">
                              All Statuses
                            </MenuItem>
                            <MenuItem value="available">
                              Available
                            </MenuItem>
                            <MenuItem value="redeemed">
                              Redeemed
                            </MenuItem>
                            <MenuItem value="disabled">
                              Disabled
                            </MenuItem>
                          </TextField>
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={2}
                        >
                          <TextField
                            select
                            name="source"
                            label="Marketplace"
                            value={filters.source}
                            onChange={handleFilterChange}
                            size="small"
                            sx={sharedTextFieldSx}
                          >
                            <MenuItem value="all">
                              All Marketplaces
                            </MenuItem>
  
                            {filterOptions.sources.map(
                              (source) => (
                                <MenuItem
                                  key={source}
                                  value={source}
                                >
                                  {source}
                                </MenuItem>
                              )
                            )}
                          </TextField>
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={2}
                        >
                          <TextField
                            select
                            name="sku"
                            label="Product"
                            value={filters.sku}
                            onChange={handleFilterChange}
                            size="small"
                            sx={sharedTextFieldSx}
                          >
                            <MenuItem value="all">
                              All Products
                            </MenuItem>
  
                            {products.map((product) => (
                              <MenuItem
                                key={product.SKU}
                                value={String(product.SKU)}
                              >
                                {product.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
  
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={2}
                        >
                          <TextField
                            name="batchId"
                            label="Batch ID"
                            value={filters.batchId}
                            onChange={handleFilterChange}
                            size="small"
                            sx={sharedTextFieldSx}
                          />
                        </Grid>
                      </Grid>
  
                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={1}
                        >
                          <Button
                            type="submit"
                            startIcon={
                              <SearchRoundedIcon />
                            }
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor:
                                "rgba(255,255,255,0.07)",
                              border:
                                "1px solid rgba(255,255,255,0.10)",
                            }}
                          >
                            Apply Filters
                          </Button>
  
                          <Button
                            type="button"
                            onClick={clearFilters}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color3,
                            }}
                          >
                            Clear Filters
                          </Button>
                        </Stack>
  
                        <Button
                          type="button"
                          onClick={exportFilteredCodes}
                          disabled={exporting}
                          startIcon={
                            exporting ? (
                              <CircularProgress
                                size={18}
                                sx={{
                                  color: "inherit",
                                }}
                              />
                            ) : (
                              <DownloadRoundedIcon />
                            )
                          }
                          sx={{
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: style.color4,
                            backgroundColor:
                              "rgba(255,255,255,0.05)",
                            border:
                              "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          Export Filtered CSV
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
  
                  <Paper
                    elevation={0}
                    sx={{
                      ...sectionCardSx,
                      p: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                      }}
                    >
                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={1}
                        alignItems={{
                          xs: "flex-start",
                          sm: "center",
                        }}
                        justifyContent="space-between"
                      >
                        <Typography
                          sx={{
                            color: style.color4,
                            fontSize: "1.2rem",
                            fontWeight: 800,
                          }}
                        >
                          Redemption Codes
                        </Typography>
  
                        <Chip
                          label={`${pagination.total} matching`}
                          size="small"
                          sx={{
                            color: style.color3,
                            backgroundColor:
                              "rgba(255,255,255,0.05)",
                          }}
                        />
                      </Stack>
                    </Box>
  
                    <Divider
                      sx={{
                        borderColor:
                          "rgba(255,255,255,0.08)",
                      }}
                    />
  
                    <TableContainer
                      sx={{
                        maxHeight: 720,
                      }}
                    >
                      <Table
                        stickyHeader
                        size="small"
                        sx={{
                          minWidth: 1250,
  
                          "& .MuiTableCell-root": {
                            borderColor:
                              "rgba(255,255,255,0.07)",
                          },
  
                          "& .MuiTableCell-head": {
                            color: style.color4,
                            backgroundColor:
                              style.color2,
                            fontWeight: 800,
                          },
  
                          "& .MuiTableCell-body": {
                            color: style.color3,
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Code</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell>
                              Marketplace
                            </TableCell>
                            <TableCell>Batch</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>
                              Redeemed By
                            </TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Redeemed</TableCell>
                            <TableCell align="right">
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
  
                        <TableBody>
                          {loading ? (
                            <TableRow>
                              <TableCell
                                colSpan={9}
                                align="center"
                                sx={{
                                  py: 6,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <CircularProgress
                                    size={22}
                                    sx={{
                                      color:
                                        style.accentGreen,
                                    }}
                                  />
  
                                  <Typography
                                    sx={{
                                      color: style.color3,
                                    }}
                                  >
                                    Loading codes...
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ) : codes.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={9}
                                align="center"
                                sx={{
                                  py: 6,
                                }}
                              >
                                No redemption codes matched
                                the current filters.
                              </TableCell>
                            </TableRow>
                          ) : (
                            codes.map((code) => {
                              const statusChip =
                                getStatusChip(
                                  code.status
                                );
  
                              const product =
                                productMap.get(
                                  String(code.sku)
                                );
  
                              const isUpdating =
                                updatingCodeId === code._id;
  
                              return (
                                <TableRow
                                  key={code._id}
                                  hover
                                >
                                  <TableCell>
                                    <Typography
                                      component="code"
                                      sx={{
                                        color: style.color4,
                                        fontFamily:
                                          "monospace",
                                        fontWeight: 700,
                                        fontSize: "0.84rem",
                                        overflowWrap:
                                          "anywhere",
                                      }}
                                    >
                                      {code.code ||
                                        "Plain code removed"}
                                    </Typography>
                                  </TableCell>
  
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        color: style.color4,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {code.productName ||
                                        product?.name ||
                                        code.sku}
                                    </Typography>
  
                                    <Typography
                                      sx={{
                                        color: style.color3,
                                        fontSize: "0.78rem",
                                      }}
                                    >
                                      SKU: {code.sku}
                                    </Typography>
                                  </TableCell>
  
                                  <TableCell>
                                    {code.source}
                                  </TableCell>
  
                                  <TableCell>
                                    <Typography
                                      title={code.batchId}
                                      sx={{
                                        maxWidth: 230,
                                        overflow: "hidden",
                                        textOverflow:
                                          "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {code.batchId}
                                    </Typography>
                                  </TableCell>
  
                                  <TableCell>
                                    <Chip
                                      label={statusChip.label}
                                      size="small"
                                      sx={{
                                        color:
                                          statusChip.color,
                                        backgroundColor:
                                          statusChip.backgroundColor,
                                        border: `1px solid ${statusChip.borderColor}`,
                                        fontWeight: 800,
                                      }}
                                    />
                                  </TableCell>
  
                                  <TableCell>
                                    {code.redeemedByEmail ||
                                      "—"}
                                  </TableCell>
  
                                  <TableCell>
                                    {formatDate(
                                      code.createdAt
                                    )}
                                  </TableCell>
  
                                  <TableCell>
                                    {formatDate(
                                      code.redeemedAt
                                    )}
                                  </TableCell>
  
                                  <TableCell align="right">
                                    {isUpdating ? (
                                      <CircularProgress
                                        size={20}
                                        sx={{
                                          color:
                                            style.accentGreen,
                                        }}
                                      />
                                    ) : (
                                      <Stack
                                        direction="row"
                                        spacing={0.25}
                                        justifyContent="flex-end"
                                      >
                                        {code.code && (
                                          <Tooltip title="Copy code">
                                            <IconButton
                                              onClick={() =>
                                                copyText(
                                                  code.code,
                                                  "Code copied."
                                                )
                                              }
                                              size="small"
                                              sx={{
                                                color:
                                                  style.color3,
                                              }}
                                            >
                                              <ContentCopyRoundedIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
  
                                        {code.status ===
                                          "available" && (
                                          <Tooltip title="Disable code">
                                            <IconButton
                                              onClick={() =>
                                                openDisableDialog(
                                                  code
                                                )
                                              }
                                              size="small"
                                              sx={{
                                                color:
                                                  "rgb(255,130,130)",
                                              }}
                                            >
                                              <BlockRoundedIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
  
                                        {code.status ===
                                          "disabled" && (
                                          <Tooltip title="Make available">
                                            <IconButton
                                              onClick={() =>
                                                updateCodeStatus(
                                                  {
                                                    code,
                                                    status:
                                                      "available",
                                                  }
                                                )
                                              }
                                              size="small"
                                              sx={{
                                                color:
                                                  "rgb(80,225,125)",
                                              }}
                                            >
                                              <CheckCircleRoundedIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Stack>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
  
                    <Divider
                      sx={{
                        borderColor:
                          "rgba(255,255,255,0.08)",
                      }}
                    />
  
                    <Box
                      sx={{
                        p: 1.5,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Pagination
                        count={pagination.pages || 1}
                        page={pagination.page || 1}
                        onChange={handlePageChange}
                        disabled={loading}
                        sx={{
                          "& .MuiPaginationItem-root": {
                            color: style.color3,
                          },
  
                          "& .Mui-selected": {
                            color: `${style.color4} !important`,
                            backgroundColor:
                              "rgba(255,255,255,0.12) !important",
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Box>
  
        <Dialog
          open={disableDialog.open}
          onClose={closeDisableDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              backgroundColor: style.color1,
              color: style.color4,
              border:
                "1px solid rgba(255,255,255,0.10)",
            },
          }}
        >
          <DialogTitle>
            Disable Redemption Code?
          </DialogTitle>
  
          <DialogContent>
            <Stack
              spacing={1.5}
              sx={{
                mt: 1,
              }}
            >
              <Typography
                sx={{
                  color: style.color3,
                }}
              >
                This prevents the code from being redeemed.
                It does not remove any license that has
                already been created.
              </Typography>
  
              <Typography
                component="code"
                sx={{
                  color: style.color4,
                  fontFamily: "monospace",
                  fontWeight: 700,
                  overflowWrap: "anywhere",
                }}
              >
                {disableDialog.code?.code ||
                  "Plain code unavailable"}
              </Typography>
  
              <TextField
                label="Reason"
                value={disableDialog.reason}
                onChange={(event) =>
                  setDisableDialog((previous) => ({
                    ...previous,
                    reason: event.target.value,
                  }))
                }
                placeholder="Refund, chargeback, invalid batch..."
                multiline
                minRows={2}
                fullWidth
                size="small"
                sx={sharedTextFieldSx}
              />
            </Stack>
          </DialogContent>
  
          <DialogActions>
            <Button
              onClick={closeDisableDialog}
              sx={{
                color: style.color3,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
  
            <Button
              onClick={() =>
                updateCodeStatus({
                  code: disableDialog.code,
                  status: "disabled",
                  reason: disableDialog.reason,
                })
              }
              disabled={
                updatingCodeId ===
                disableDialog.code?._id
              }
              startIcon={
                updatingCodeId ===
                disableDialog.code?._id ? (
                  <CircularProgress
                    size={18}
                    sx={{
                      color: "inherit",
                    }}
                  />
                ) : (
                  <BlockRoundedIcon />
                )
              }
              sx={{
                borderRadius: "5px",
                textTransform: "none",
                fontWeight: 700,
                color: "#ffb4b4",
                backgroundColor:
                  "rgba(220,70,70,0.14)",
              }}
            >
              Disable Code
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }